import { setTrackingId } from "client/Misc/utils";
import {
	forwardRef,
	Ref,
	PropsWithChildren,
	ReactElement,
	useCallback
} from "react";
import styles from "./DocumentPageLayout.module.less";
import { DocumentLayoutKind } from "client/Api/Documents/documents";
import { IconButton } from "client/Components";

export type DocumentPageLayoutProps = {
	trackingId: string;
};

const DocumentPageLayoutInternal = forwardRef<HTMLDivElement, PropsWithChildren<DocumentPageLayoutProps>>(
	(props: PropsWithChildren<DocumentPageLayoutProps>, ref: Ref<HTMLDivElement>): ReactElement => {
		const {
			trackingId,
			children
		} = props;

		return (
			<div
				{...setTrackingId(trackingId)}
				className={styles.root}
				ref={ref}
			>
				{children}
			</div>
		);
	}
);
DocumentPageLayoutInternal.displayName = "DocumentPageLayout";

export const Header = (props: PropsWithChildren<{}>): ReactElement => {
	const {
		children
	} = props;

	return (
		<div
			className={styles.header}
		>
			{children}
		</div>
	);
};
Header.displayName = "DocumentPageLayout.Header";

export const Content = (props: PropsWithChildren<{}>): ReactElement => {
	const {
		children
	} = props;

	return (
		<div
			className={styles.content}
		>
			{children}
		</div>
	);
};
Content.displayName = "DocumentPageLayout.Content";

export const BreadCrumb = (props: PropsWithChildren<{}>): ReactElement => {
	const {
		children
	} = props;

	return (
		<div
			className={styles.breadCrumb}
		>
			{children}
		</div>
	);
};
BreadCrumb.displayName = "DocumentPageLayout.BreadCrumb";

export const ActionBarContainer = (props: PropsWithChildren<{}>): ReactElement => {
	const {
		children
	} = props;

	return (
		<div
			className={styles.actionBarContainer}
		>
			{children}
		</div>
	);
};
ActionBarContainer.displayName = "DocumentPageLayout.ActionBarContainer";

const SortAndFilter = (props: PropsWithChildren<{}>): ReactElement => {
	const {
		children
	} = props;

	return (
		<div className={styles.sortFilterSection}>
			{children}
		</div>
	);
};
SortAndFilter.displayName = "DocumentPageLayout.SortAndFilter";

export type LayoutKindChangeButtonProps = {
	layoutKind: DocumentLayoutKind;
	onLayoutKindChange(view: DocumentLayoutKind): void;
	isDisabled?: boolean;
};

export const LayoutKindChangeButton = (props: LayoutKindChangeButtonProps): ReactElement => {
	const {
		layoutKind,
		onLayoutKindChange,
		isDisabled = false
	} = props;

	const clickHandler = useCallback(
		(): void => {
			onLayoutKindChange(layoutKind === DocumentLayoutKind.List
				? DocumentLayoutKind.Grid
				: DocumentLayoutKind.List
			);
		},
		[
			onLayoutKindChange,
			layoutKind
		]
	);

	return (
		<IconButton
			icon={layoutKind === DocumentLayoutKind.List
				? "grid-2"
				: "bars"
			}
			kind="secondary"
			trackingId="change-view-button"
			onClick={clickHandler}
			size="large"
			isDisabled={isDisabled}
		/>
	);
};

LayoutKindChangeButton.displayName = "LayoutKindChangeButton";

type DocumentPageLayoutWithSubComponents = typeof DocumentPageLayoutInternal & {
	Header: typeof Header;
	Content: typeof Content;
	BreadCrumb: typeof BreadCrumb;
	ActionBarContainer: typeof ActionBarContainer;
	SortAndFilter: typeof SortAndFilter;
	LayoutKindChangeButton: typeof LayoutKindChangeButton;
};

export const DocumentPageLayout: DocumentPageLayoutWithSubComponents = DocumentPageLayoutInternal as unknown as DocumentPageLayoutWithSubComponents;
DocumentPageLayout.Header = Header;
DocumentPageLayout.Content = Content;
DocumentPageLayout.BreadCrumb = BreadCrumb;
DocumentPageLayout.ActionBarContainer = ActionBarContainer;
DocumentPageLayout.SortAndFilter = SortAndFilter;
DocumentPageLayout.LayoutKindChangeButton = LayoutKindChangeButton;